import React, { useState, useEffect } from 'react';
import AdminLayout from '../layouts/AdminLayout';
import adminApi from '../api/adminApi';
import { FaPlus, FaEdit, FaTrash, FaSearch } from 'react-icons/fa';

const EMPTY = { title: '', slug: '', excerpt: '', content: '', author: '', category: '', tags: '', imageUrl: '', readTimeMinutes: 5, isPublished: true, publishedAt: new Date().toISOString().split('T')[0] };

const CATEGORIES = ['Cancer Awareness', 'Cancer Treatment', 'Treatment Innovation', 'Liver Health', 'Patient Care', 'Gastroenterology', 'Surgery', 'Nutrition'];

const AdminBlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [error, setError] = useState('');

  const fetchPosts = async () => {
    try { const res = await adminApi.get('/blog/all'); setPosts(res.data); }
    catch { setError('Failed to load blog posts.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPosts(); }, []);

  const autoSlug = t => t.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  const openAdd = () => { setForm(EMPTY); setEditId(null); setModal('add'); setError(''); };
  const openEdit = post => {
    setForm({
      title: post.title, slug: post.slug, excerpt: post.excerpt, content: post.content,
      author: post.author, category: post.category, tags: post.tags || '',
      imageUrl: post.imageUrl || '', readTimeMinutes: post.readTimeMinutes,
      isPublished: post.isPublished,
      publishedAt: post.publishedAt ? post.publishedAt.split('T')[0] : new Date().toISOString().split('T')[0],
    });
    setEditId(post.id); setModal('edit'); setError('');
  };
  const closeModal = () => { setModal(null); setError(''); };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    if (name === 'title') { setForm(p => ({ ...p, title: value, slug: p.slug || autoSlug(value) })); return; }
    setForm(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async () => {
    if (!form.title || !form.author) { setError('Title and Author are required.'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form, readTimeMinutes: parseInt(form.readTimeMinutes) || 5, publishedAt: new Date(form.publishedAt).toISOString() };
      if (modal === 'add') await adminApi.post('/blog', payload);
      else await adminApi.put(`/blog/${editId}`, payload);
      await fetchPosts();
      closeModal();
    } catch (err) { setError(err.response?.data?.message || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await adminApi.delete(`/blog/${deleteId}`); setDeleteId(null); await fetchPosts(); }
    catch { setError('Failed to delete.'); setDeleteId(null); }
  };

  const filtered = posts.filter(p =>
    p.title?.toLowerCase().includes(search.toLowerCase()) ||
    p.author?.toLowerCase().includes(search.toLowerCase()) ||
    p.category?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminLayout title="Blog Posts" subtitle="Manage health articles and publications">
      <div className="admin-card" id="admin-blog-card">
        <div className="admin-card-header">
          <div className="admin-card-title">All Posts ({filtered.length})</div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <div className="admin-search">
              <FaSearch className="admin-search-icon" />
              <input id="blog-search" placeholder="Search posts…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <button className="admin-btn admin-btn-primary" onClick={openAdd} id="blog-add-btn"><FaPlus /> New Post</button>
          </div>
        </div>

        <div className="admin-table-wrapper">
          <table className="admin-table" id="blog-table">
            <thead><tr><th>Image</th><th>Title</th><th>Author</th><th>Category</th><th>Read Time</th><th>Published</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8}><div className="admin-empty"><div className="admin-empty-icon">📝</div><div className="admin-empty-text">No blog posts found.</div></div></td></tr>
              ) : filtered.map(post => (
                <tr key={post.id} id={`blog-row-${post.id}`}>
                  <td><img src={post.imageUrl || 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=100&h=60&fit=crop'} alt={post.title} className="admin-table-img-sq" /></td>
                  <td>
                    <div style={{ fontWeight: 600, maxWidth: 240 }}>{post.title}</div>
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '0.2rem' }}>{post.slug}</div>
                  </td>
                  <td style={{ fontSize: '0.85rem' }}>{post.author}</td>
                  <td><span style={{ background: '#f1f5f9', padding: '0.2rem 0.6rem', borderRadius: 6, fontSize: '0.75rem', color: '#475569', fontWeight: 600 }}>{post.category}</span></td>
                  <td style={{ textAlign: 'center', fontSize: '0.85rem' }}>{post.readTimeMinutes} min</td>
                  <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{post.publishedAt ? new Date(post.publishedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</td>
                  <td><span className={`admin-badge ${post.isPublished ? 'published' : 'draft'}`}>{post.isPublished ? '● Published' : '● Draft'}</span></td>
                  <td>
                    <div className="admin-actions">
                      <button className="admin-btn admin-btn-edit admin-btn-sm" onClick={() => openEdit(post)} id={`edit-blog-${post.id}`}><FaEdit /></button>
                      <button className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => setDeleteId(post.id)} id={`delete-blog-${post.id}`}><FaTrash /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {(modal === 'add' || modal === 'edit') && (
        <div className="admin-modal-overlay" onClick={closeModal}>
          <div className="admin-modal admin-modal-lg" onClick={e => e.stopPropagation()} id="blog-modal">
            <div className="admin-modal-header">
              <div className="admin-modal-title">{modal === 'add' ? 'New Blog Post' : 'Edit Blog Post'}</div>
              <button className="admin-modal-close" onClick={closeModal}>✕</button>
            </div>
            <div className="admin-modal-body">
              {error && <div className="admin-login-error" style={{ marginBottom: '1rem' }}>{error}</div>}
              <div className="admin-form-grid">
                <div className="admin-form-group full">
                  <label className="admin-form-label" htmlFor="blog-title">Title *</label>
                  <input id="blog-title" name="title" className="admin-form-input" placeholder="Article title…" value={form.title} onChange={handleChange} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label" htmlFor="blog-slug">Slug</label>
                  <input id="blog-slug" name="slug" className="admin-form-input" placeholder="auto-generated" value={form.slug} onChange={handleChange} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label" htmlFor="blog-author">Author *</label>
                  <input id="blog-author" name="author" className="admin-form-input" placeholder="Dr. Name" value={form.author} onChange={handleChange} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label" htmlFor="blog-category">Category</label>
                  <select id="blog-category" name="category" className="admin-form-select" value={form.category} onChange={handleChange}>
                    <option value="">Select category…</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label" htmlFor="blog-readtime">Read Time (min)</label>
                  <input id="blog-readtime" name="readTimeMinutes" type="number" className="admin-form-input" placeholder="5" value={form.readTimeMinutes} onChange={handleChange} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label" htmlFor="blog-date">Publish Date</label>
                  <input id="blog-date" name="publishedAt" type="date" className="admin-form-input" value={form.publishedAt} onChange={handleChange} />
                </div>
                <div className="admin-form-group full">
                  <label className="admin-form-label" htmlFor="blog-image">Image URL</label>
                  <input id="blog-image" name="imageUrl" className="admin-form-input" placeholder="https://…" value={form.imageUrl} onChange={handleChange} />
                </div>
                <div className="admin-form-group full">
                  <label className="admin-form-label" htmlFor="blog-tags">Tags (comma-separated)</label>
                  <input id="blog-tags" name="tags" className="admin-form-input" placeholder="cancer,liver,surgery" value={form.tags} onChange={handleChange} />
                </div>
                <div className="admin-form-group full">
                  <label className="admin-form-label" htmlFor="blog-excerpt">Excerpt</label>
                  <textarea id="blog-excerpt" name="excerpt" className="admin-form-textarea" rows={2} placeholder="Short summary shown in blog listing…" value={form.excerpt} onChange={handleChange} />
                </div>
                <div className="admin-form-group full">
                  <label className="admin-form-label" htmlFor="blog-content">Full Content</label>
                  <textarea id="blog-content" name="content" className="admin-form-textarea" rows={8} placeholder="Write the full article content here…" value={form.content} onChange={handleChange} />
                </div>
                <div className="admin-form-group full">
                  <label className="admin-toggle" htmlFor="blog-published">
                    <input id="blog-published" type="checkbox" name="isPublished" checked={form.isPublished} onChange={handleChange} />
                    <div className="admin-toggle-track"><div className="admin-toggle-thumb" /></div>
                    <span className="admin-form-label" style={{ marginBottom: 0 }}>Published (visible on website)</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="admin-btn admin-btn-primary" onClick={handleSave} disabled={saving} id="blog-modal-save">
                {saving ? '⏳ Saving…' : modal === 'add' ? '✅ Publish Post' : '✅ Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="admin-modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="admin-modal admin-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-body" style={{ textAlign: 'center', padding: '2.5rem 2rem' }}>
              <div className="admin-confirm-icon">🗑️</div>
              <div className="admin-confirm-title">Delete Blog Post?</div>
              <p className="admin-confirm-text">This article will be permanently removed from the website.</p>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn-ghost" onClick={() => setDeleteId(null)}>Cancel</button>
              <button className="admin-btn admin-btn-danger" onClick={handleDelete}>🗑️ Delete</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminBlogPage;
